import React, { useState, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import DateRangeFilter from './DateRangeFilter';
import {
  fetchTasksInRange, processUserConsumptionData, getPresetDates,
  TOOLTIP_STYLE, AXIS_TICK, CURVE_TYPES, formatSeconds,
} from './telemetryUtils';

const UserConsumption = () => {
  const { apiCall } = useApi();
  const [preset, setPreset] = useState('month');
  const [startDate, setStartDate] = useState(() => getPresetDates('month').start);
  const [endDate, setEndDate] = useState(() => dayjs());
  const [userIdFilter, setUserIdFilter] = useState('');
  const [curveType, setCurveType] = useState('linear');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rawTasks, setRawTasks] = useState(null);
  const [data, setData] = useState(null);

  const handleApply = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tasks = await fetchTasksInRange(apiCall, startDate, endDate);
      setRawTasks(tasks);
      const filtered = userIdFilter
        ? tasks.filter((t) => t.user_id && t.user_id.includes(userIdFilter))
        : tasks;
      const processed = processUserConsumptionData(filtered);
      setData(processed);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [apiCall, startDate, endDate, userIdFilter]);

  // Re-process locally when userIdFilter changes and we already have fetched data
  const handleUserIdChange = useCallback((val) => {
    setUserIdFilter(val);
    if (rawTasks) {
      const filtered = val
        ? rawTasks.filter((t) => t.user_id && t.user_id.includes(val))
        : rawTasks;
      setData(processUserConsumptionData(filtered));
    }
  }, [rawTasks]);

  // Credits formula: script_char_count → credits_deducted_seconds
  const currentCreditsCurve = useMemo(() => {
    if (!data?.creditsCurves) return null;
    return data.creditsCurves[curveType];
  }, [data, curveType]);

  const renderCreditsFormula = () => {
    if (!data || !currentCreditsCurve) return null;
    const r = currentCreditsCurve;
    const MIN_SAMPLES = 2;

    const bestCurve = CURVE_TYPES.reduce((best, ct) => {
      const c = data.creditsCurves[ct.key];
      return (c.sampleCount >= MIN_SAMPLES && c.r2 > (best?.r2 || -1)) ? { ...ct, r2: c.r2 } : best;
    }, null);

    const hasSamples = r.sampleCount >= MIN_SAMPLES;
    const estimate = hasSamples && data.avgChars
      ? (() => { const v = r.predict(data.avgChars); return v > 0 ? Math.round(v * 10) / 10 : 0; })()
      : null;

    return (
      <div className="formula-card">
        <div className="formula-header">
          <h3 className="card-title">Derived Credits Formula (chars → deducted seconds)</h3>
          <div className="curve-selector">
            {CURVE_TYPES.map((ct) => (
              <button
                key={ct.key}
                className={`filter-preset-btn${curveType === ct.key ? ' active' : ''}`}
                onClick={() => setCurveType(ct.key)}
              >
                {ct.label}
              </button>
            ))}
          </div>
        </div>
        {bestCurve && bestCurve.key !== curveType && (
          <div className="formula-best-hint">
            Best fit: <button className="formula-best-link" onClick={() => setCurveType(bestCurve.key)}>
              {bestCurve.label}
            </button> (R²={Math.round(bestCurve.r2 * 1000) / 1000})
          </div>
        )}
        <div className="formula-items">
          <div className="formula-item">
            <span className="formula-label">
              Combined <span className="formula-samples">
                {hasSamples ? `(${r.sampleCount} samples, R²=${Math.round(r.r2 * 1000) / 1000}${(data.creditsOutliers || 0) > 0 ? `, ${data.creditsOutliers} outliers removed` : ''})` : ''}
              </span>
            </span>
            <span className={`formula-value${!hasSamples ? ' formula-insufficient' : ''}`}>
              {hasSamples ? r.formula() : (r.sampleCount === 0 ? 'No data' : `Insufficient data (${r.sampleCount} sample, need ${MIN_SAMPLES}+)`)}
            </span>
            {estimate != null && (
              <span className="formula-estimate">@ {data.avgChars} avg chars = {formatSeconds(estimate)}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <Loader />;

  return (
    <div>
      <DateRangeFilter
        preset={preset}
        setPreset={setPreset}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onApply={handleApply}
        showUserFilter
        userIdFilter={userIdFilter}
        setUserIdFilter={handleUserIdChange}
      />

      {error && <div className="error-message">{error}</div>}

      {!data && !error && (
        <div className="telemetry-empty">Select a date range and click Apply to load consumption data.</div>
      )}

      {data && data.totalTasks === 0 && (
        <div className="telemetry-empty">No completed tasks found for the selected period.</div>
      )}

      {data && data.totalTasks > 0 && (
        <>
          {/* Summary Stats */}
          <div className="telemetry-stats-grid-6">
            <div className="stat-card">
              <p className="stat-label">Unique Users</p>
              <p className="stat-value">{data.totalUsers}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Total Tasks</p>
              <p className="stat-value">{data.totalTasks}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Avg Tasks/User</p>
              <p className="stat-value">{data.avgTasksPerUser}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Credits Deducted</p>
              <p className="stat-value stat-yellow">{data.totalCreditsDeducted}s</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Credits Estimated</p>
              <p className="stat-value" style={{ color: '#3b82f6' }}>{data.totalCreditsEstimated}s</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Credit Accuracy</p>
              <p className="stat-value" style={{ color: data.creditAccuracy >= 0.9 && data.creditAccuracy <= 1.1 ? '#4ade80' : '#f87171' }}>
                {data.creditAccuracy != null ? `${data.creditAccuracy}x` : '—'}
              </p>
            </div>
          </div>

          {/* Credits Formula */}
          {renderCreditsFormula()}

          {/* Chart 1: Tasks per User (top 20, horizontal) */}
          <div className="chart-card">
            <h3 className="card-title">Tasks per User (Top 20)</h3>
            <ResponsiveContainer width="100%" height={Math.max(400, data.perUser.slice(0, 20).length * 32)}>
              <BarChart data={data.perUser.slice(0, 20)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                <XAxis type="number" stroke="#6b7280" tick={AXIS_TICK} />
                <YAxis
                  dataKey={(u) => `${u.email || '—'} · ${u.userId.slice(0, 10)}…`}
                  type="category"
                  width={260}
                  stroke="#6b7280"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#9ca3af' }}
                  formatter={(value, name, props) => {
                    const user = props.payload;
                    const label = `${user.email || '(no email)'} — ${user.userId}`;
                    return [`${value} tasks (avg ${user.avgChars} chars)`, label];
                  }}
                />
                <Bar dataKey="taskCount" fill="#f97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="charts-grid">
            {/* Chart 2: Avg & Total Characters per User */}
            <div className="chart-card">
              <h3 className="card-title">Characters per User (Top 20)</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.perUser.slice(0, 20).map((u) => ({
                  userId: `${u.email || '—'} · ${u.userId.slice(0, 8)}…`,
                  fullUserId: u.userId,
                  email: u.email,
                  avgChars: u.avgChars,
                  totalChars: u.totalChars,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                  <XAxis
                    dataKey="userId"
                    stroke="#6b7280"
                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis stroke="#6b7280" tick={AXIS_TICK} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#9ca3af' }} />
                  <Legend wrapperStyle={{ color: '#9ca3af' }} />
                  <Bar dataKey="avgChars" name="Avg Chars" fill="#f97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="totalChars" name="Total Chars" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Chart 3: Credits Estimated vs Deducted */}
            <div className="chart-card">
              <h3 className="card-title">Credits: Estimated vs Deducted (Top 20)</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.creditComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                  <XAxis
                    dataKey="userId"
                    stroke="#6b7280"
                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    stroke="#6b7280"
                    tick={AXIS_TICK}
                    label={{ value: 'Seconds', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    labelStyle={{ color: '#9ca3af' }}
                    formatter={(value, name, props) => {
                      const delta = props.payload.deducted - props.payload.estimated;
                      const suffix = name === 'Deducted' ? ` (delta: ${delta > 0 ? '+' : ''}${delta}s)` : '';
                      return [`${value}s${suffix}`, name];
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#9ca3af' }} />
                  <Bar dataKey="estimated" name="Estimated" fill="#f97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="deducted" name="Deducted" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Metadata Averages Table */}
          <div className="chart-card">
            <h3 className="card-title">Metadata Averages</h3>
            <table className="telemetry-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>All Tasks</th>
                  <th>Video Only</th>
                  <th>Presentation Only</th>
                </tr>
              </thead>
              <tbody>
                {data.metadataAverages.map((row) => (
                  <tr key={row.metric}>
                    <td>{row.metric}</td>
                    <td>{row.all != null ? row.all.toLocaleString() : <span className="text-muted">N/A</span>}</td>
                    <td>{row.video != null ? row.video.toLocaleString() : <span className="text-muted">N/A</span>}</td>
                    <td>{row.presentation != null ? row.presentation.toLocaleString() : <span className="text-muted">N/A</span>}</td>
                  </tr>
                ))}
                {/* Resolution Distribution */}
                <tr>
                  <td>Resolution Distribution</td>
                  <td>
                    1080p: {data.resolutionCounts['1080p']} ({data.totalTasks > 0 ? Math.round(data.resolutionCounts['1080p'] / data.totalTasks * 100) : 0}%)
                    {' / '}
                    720p: {data.resolutionCounts['720p']} ({data.totalTasks > 0 ? Math.round(data.resolutionCounts['720p'] / data.totalTasks * 100) : 0}%)
                  </td>
                  <td colSpan={2} className="text-muted">—</td>
                </tr>
                {/* Voice Distribution */}
                <tr>
                  <td>Voice Distribution</td>
                  <td colSpan={3}>
                    {Object.entries(data.voiceCounts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([voice, count]) => (
                        <span key={voice} style={{ marginRight: '1rem' }}>
                          {voice}: {count} ({data.totalTasks > 0 ? Math.round(count / data.totalTasks * 100) : 0}%)
                        </span>
                      ))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default UserConsumption;
